// Duplicates the `metricGroups` block inside
// `apps/ui-rtk/src/constants/charts.tsx` at `VISUALIZATIONS_MAP
// .revenue_kpi_dashboard_table.props.metricGroups` (lines ~6001-6559).
// Shape differs: dynamic groups here use `resolveMetrics(prefs)` instead of
// the UI's `getMetrics(...looseArgs)` callback. Keep both copies aligned
// until the UI is refactored to consume this registry directly.

import { getGaRevenueMetrics, getRevenueBreakdownMetrics, getTotalBusinessRevenueMetrics } from './dynamic-groups';
import { buildInitiativeCategoryMetrics, buildInitiativeTypeMetrics } from './initiative-metrics';
import type { MetricGroupSpec } from './types';

export const KPI_METRIC_GROUPS = {
  adSpend: {
    title: 'Ad Spend',
    metrics: [
      { key: 'metrics.instagram_ads_spend', label: 'Instagram Ads Spend', type: 'currency' },
      { key: 'metrics.facebook_ads_spend', label: 'Facebook Ads Spend', type: 'currency' },
      { key: 'metrics.tiktok_ads_spend', label: 'TikTok Ads Spend', type: 'currency' },
      { key: 'metrics.pinterest_ads_spend', label: 'Pinterest Ads Spend', type: 'currency' },
      { key: 'metrics.google_ads_total_spend', label: 'Google Ads Spend', type: 'currency' },
      { key: 'metrics.amazon_ads_total_spend', label: 'Amazon Ads Spend', type: 'currency' },
      { key: 'metrics.total_ad_spend', label: 'Total Ad Spend', type: 'currency' },
      { key: 'metrics.spend_percentage_of_new_customer_revenue', label: 'Spend % of New Customer Revenue', type: 'percent' },
      { key: 'metrics.amer', label: 'aMER', type: 'decimal', suffix: 'x' },
    ],
    showTotals: true,
  },
  // Mirrors the `mer` group added to charts.tsx by PR #4012 (feat: add MER
  // metrics table to KPI Dashboard). Present here so the metric catalog offers
  // these MER measures to custom reports, and listed in
  // BUILTIN_DEFAULT_GROUP_ORDER (right after `adSpend`) so the default report
  // matches DEFAULT_TABLE_SETTINGS.order — the default-order sync spec enforces this.
  mer: {
    title: 'Marketing Efficiency Ratio (MER)',
    metrics: [
      { key: 'metrics.dtc_ecomm_mer', label: 'DTC Ecomm MER', type: 'decimal', suffix: 'x' },
      { key: 'metrics.dtc_ecomm_amer', label: 'DTC Ecomm aMER', type: 'decimal', suffix: 'x' },
      { key: 'metrics.total_dtc_mer', label: 'Total DTC MER', type: 'decimal', suffix: 'x' },
      { key: 'metrics.total_dtc_amer', label: 'Total DTC aMER', type: 'decimal', suffix: 'x' },
      { key: 'metrics.total_business_mer', label: 'Total Business MER', type: 'decimal', suffix: 'x' },
      { key: 'metrics.dtc_ecomm_plus_amazon_mer', label: 'DTC Ecomm + Amazon MER', type: 'decimal', suffix: 'x' },
    ],
    showTotals: false,
  },
  croWebMerch: {
    title: 'CRO/Web Merch KPIs',
    metrics: [
      { key: 'metrics.baseline_per_session_value', label: 'Baseline Per Session Value', type: 'currency' },
      { key: 'metrics.triggered_per_session_value', label: 'Triggered Per Session Value', type: 'currency' },
      { key: 'metrics.paid_per_session_value', label: 'Paid Per Session Value', type: 'currency' },
      { key: 'metrics.total_per_session_value', label: 'Total Per Session Value', type: 'currency' },
    ],
    showTotals: true,
  },
  searchKPIs: {
    title: 'Search KPIs',
    metrics: [
      { key: 'metrics.ga_sessions_from_organic_sessions', label: 'Sessions from Organic Search', type: 'number' },
      { key: 'metrics.google_ads_paid_branded_search_clicks', label: 'Sessions from Paid Branded Search', type: 'number' },
      {
        key: 'metrics.ga_sessions_from_organic_plus_paid_branded_search_sessions',
        label: 'Total Sessions from Organic + Paid Branded Search',
        type: 'number',
      },
      { key: 'metrics.ga_revenue_from_organic_sessions', label: 'Revenue from Organic Search', type: 'currency' },
      { key: 'metrics.google_ads_paid_branded_search_revenue', label: 'Revenue from Paid Branded Search', type: 'currency' },
      {
        key: 'metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue',
        label: 'Total Revenue from Organic + Paid Branded Search',
        type: 'currency',
      },
      { key: 'metrics.organic_per_session_value', label: 'Revenue per Session from Organic Search', type: 'currency' },
      {
        key: 'metrics.google_ads_paid_branded_search_revenue_per_click',
        label: 'Revenue per Session from Paid Branded Search',
        type: 'currency',
      },
      {
        key: 'metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue_per_session',
        label: 'Total Rev per Session from Organic + Paid Branded Search',
        type: 'currency',
      },
    ],
    showTotals: false,
  },
  brand: {
    title: 'Brand Search KPIs',
    metrics: [
      { key: 'metrics.google_search_organic_branded_search_impressions', label: 'Branded Searches', type: 'number' },
      { key: 'metrics.total_google_search_clicks', label: 'Total Branded Search Clicks', type: 'number' },
      { key: 'metrics.branded_search_conversion_rate', label: 'Search Conversion Rate', type: 'percent' },
    ],
    showTotals: false,
  },
  traffic: {
    title: 'Traffic',
    metrics: [
      { key: 'metrics.ga_paid_sessions', label: 'Paid Sessions', type: 'number' },
      { key: 'metrics.ga_triggered_sessions', label: 'Triggered Sessions', type: 'number' },
      { key: 'metrics.ga_baseline_sessions', label: 'Baseline Sessions', type: 'number' },
      { key: 'metrics.ga_sessions', label: 'Total Sessions', type: 'number' },
    ],
    showTotals: true,
  },
  // Catalog-only group (intentionally NOT in BUILTIN_DEFAULT_GROUP_ORDER): GA4
  // web-funnel viewer counts (active users per funnel step), broken out by
  // traffic attribution. Available to add to custom reports via the metric
  // catalog.
  webFunnelViewers: {
    title: 'Web Funnel Viewers',
    metrics: [
      { key: 'metrics.ga_total_homepage_viewers', label: 'Homepage Viewers — Total', type: 'number' },
      { key: 'metrics.ga_paid_homepage_viewers', label: 'Homepage Viewers — Paid', type: 'number' },
      { key: 'metrics.ga_organic_homepage_viewers', label: 'Homepage Viewers — Organic', type: 'number' },
      { key: 'metrics.ga_triggered_homepage_viewers', label: 'Homepage Viewers — Triggered', type: 'number' },
      { key: 'metrics.ga_total_collection_viewers', label: 'Collection Viewers — Total', type: 'number' },
      { key: 'metrics.ga_paid_collection_viewers', label: 'Collection Viewers — Paid', type: 'number' },
      { key: 'metrics.ga_organic_collection_viewers', label: 'Collection Viewers — Organic', type: 'number' },
      { key: 'metrics.ga_triggered_collection_viewers', label: 'Collection Viewers — Triggered', type: 'number' },
      { key: 'metrics.ga_total_product_page_viewers', label: 'Product Page Viewers — Total', type: 'number' },
      { key: 'metrics.ga_paid_product_page_viewers', label: 'Product Page Viewers — Paid', type: 'number' },
      { key: 'metrics.ga_organic_product_page_viewers', label: 'Product Page Viewers — Organic', type: 'number' },
      { key: 'metrics.ga_triggered_product_page_viewers', label: 'Product Page Viewers — Triggered', type: 'number' },
    ],
    showTotals: false,
  },
  averageOrderValues: {
    title: 'Average Order Values',
    metrics: [
      { key: 'metrics.shopify_baseline_aov', label: 'Baseline AOV', type: 'currency' },
      { key: 'metrics.shopify_triggered_aov', label: 'Triggered AOV', type: 'currency' },
      { key: 'metrics.shopify_paid_aov', label: 'Paid AOV', type: 'currency' },
      { key: 'metrics.shopify_total_aov', label: 'Total AOV', type: 'currency' },
    ],
    showTotals: false,
  },
  totalBusinessRevenue: {
    title: 'Total Business Revenue',
    isDynamic: true,
    resolveMetrics: getTotalBusinessRevenueMetrics,
    showTotals: true,
  },
  gaRevenue: {
    title: 'GA Revenue by Traffic Source',
    isDynamic: true,
    resolveMetrics: getGaRevenueMetrics,
    showTotals: true,
  },
  shopifyRevenue: {
    title: 'Shopify Web Revenue by Traffic Source',
    metrics: [
      { key: 'metrics.shopify_baseline_revenue', label: 'Shopify Baseline Revenue', type: 'currency' },
      { key: 'metrics.shopify_triggered_revenue', label: 'Shopify Triggered Revenue', type: 'currency' },
      { key: 'metrics.shopify_paid_revenue', label: 'Shopify Paid Revenue', type: 'currency' },
      { key: 'metrics.shopify_subscription_revenue', label: 'Shopify Subscription Revenue', type: 'currency' },
      { key: 'metrics.shopify_total_traffic_source_revenue', label: 'Total Web Revenue', type: 'currency' },
    ],
    showTotals: true,
  },
  shopifyCustomer: {
    title: 'Shopify - Total Revenue',
    metrics: [
      { key: 'metrics.shopify_total_new_customer_revenue', label: 'Shopify New Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_total_repeat_customer_revenue', label: 'Shopify Repeat Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_total_customer_revenue', label: 'Total', type: 'currency' },
    ],
    showTotals: true,
  },
  shopifyEcommRevenue: {
    title: 'Shopify - DTC Ecomm Revenue',
    metrics: [
      { key: 'metrics.shopify_ecomm_new_customer_revenue', label: 'Ecomm - New Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_ecomm_repeat_customer_revenue', label: 'Ecomm - Repeat Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_ecomm_total_customer_revenue', label: 'Ecomm - Total Revenue', type: 'currency' },
    ],
    showTotals: true,
  },
  shopifyRetailRevenue: {
    title: 'Shopify - DTC Retail Revenue',
    metrics: [
      { key: 'metrics.shopify_dtc_retail_new_customer_revenue', label: 'DTC Retail - New Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_repeat_customer_revenue', label: 'DTC Retail - Repeat Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_total_customer_revenue', label: 'DTC Retail - Total Revenue', type: 'currency' },
    ],
    showTotals: true,
  },
  // 3rd Party Retail: retailer point-of-sale sell-through at consumer prices
  // (not the brand's sell-in revenue, and not the Shopify "Third Party Retail"
  // sales channel, which is orders the brand itself records in Shopify). The
  // slug and the `wholesale_*` metric keys are kept for saved reports, table
  // order prefs and CLI JSON; only the display names say 3rd Party Retail.
  // REI is dated on its fiscal week-ending date and SPINS on its 4-week quad
  // end date (neither spread across days), so those rows and the total are
  // lumpy at daily/weekly grain by design. showTotals stays false: the 3rd
  // Party Retail Revenue row IS the rollup, and a WoW Marginal row summing
  // every row would double it (the table pins marginalMetrics to the rollup).
  wholesale: {
    title: '3rd Party Retail Revenue',
    metrics: [
      { key: 'metrics.wholesale_rei_regular_revenue', label: 'REI Regular Revenue', type: 'currency' },
      { key: 'metrics.wholesale_sams_club_regular_revenue', label: "Sam's Club Revenue", type: 'currency' },
      { key: 'metrics.wholesale_whole_foods_regular_revenue', label: 'Whole Foods Revenue', type: 'currency' },
      { key: 'metrics.wholesale_walmart_regular_revenue', label: 'Walmart Revenue', type: 'currency' },
      { key: 'metrics.wholesale_target_regular_revenue', label: 'Target Revenue', type: 'currency' },
      { key: 'metrics.wholesale_spins_regular_revenue', label: 'SPINS Retail Scan Revenue', type: 'currency' },
      { key: 'metrics.wholesale_kroger_regular_revenue', label: 'Kroger Revenue', type: 'currency' },
      { key: 'metrics.wholesale_total_revenue', label: '3rd Party Retail Revenue', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifyThirdPartyEcommRevenue: {
    title: 'Shopify - Third Party Ecomm Revenue',
    metrics: [
      {
        key: 'metrics.shopify_third_party_ecomm_new_customer_revenue',
        label: 'Third Party Ecomm - New Customer Revenue',
        type: 'currency',
      },
      {
        key: 'metrics.shopify_third_party_ecomm_repeat_customer_revenue',
        label: 'Third Party Ecomm - Repeat Customer Revenue',
        type: 'currency',
      },
      { key: 'metrics.shopify_third_party_ecomm_total_customer_revenue', label: 'Third Party Ecomm - Total Revenue', type: 'currency' },
    ],
    showTotals: true,
  },
  shopifySubscriptionRevenue: {
    title: 'Shopify - Subscription Revenue',
    metrics: [
      { key: 'metrics.shopify_subscription_new_customer_revenue', label: 'Subscription - New Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_subscription_repeat_customer_revenue', label: 'Subscription - Repeat Customer Revenue', type: 'currency' },
      { key: 'metrics.shopify_subscription_total_customer_revenue', label: 'Subscription - Total Revenue', type: 'currency' },
    ],
    showTotals: true,
  },
  amazonRevenue: {
    title: 'Amazon Revenue',
    metrics: [
      { key: 'metrics.amazon_paid_revenue', label: 'Paid', type: 'currency' },
      { key: 'metrics.amazon_owned_revenue', label: 'Owned', type: 'currency' },
      { key: 'metrics.amazon_total_revenue', label: 'Total', type: 'currency' },
    ],
    showTotals: false,
  },
  // Slug kept as `shopifyContribution` for saved table-order/report compat,
  // but the table now covers profitability across every channel. Labels state
  // each metric's channel scope explicitly; `metrics.total_contribution_margin`
  // is the legacy warehouse name for the ECOMM-scoped margin (DTC E-Commerce +
  // Subscription, carrying the digital ad spend) — see company_data_daily.js.
  // showTotals stays false: rows mix channel scopes and rollups, so a summed
  // Totals row would double-count.
  shopifyContribution: {
    title: 'Profitability',
    metrics: [
      { key: 'metrics.shopify_ecomm_gross_profit', label: 'DTC Ecomm Gross Profit', type: 'currency' },
      { key: 'metrics.shopify_subscription_gross_profit', label: 'Subscription Gross Profit', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_gross_profit', label: 'DTC Retail Gross Profit', type: 'currency' },
      { key: 'metrics.shopify_total_gross_profit', label: 'Shopify Total Gross Profit', type: 'currency' },
      { key: 'metrics.amazon_gross_profit', label: 'Amazon Gross Profit', type: 'currency' },
      { key: 'metrics.total_contribution_margin', label: 'Ecomm Contribution Margin', type: 'currency' },
      { key: 'metrics.subscription_contribution_margin', label: 'Subscription Contribution Margin', type: 'currency' },
      { key: 'metrics.dtc_retail_contribution_margin', label: 'DTC Retail Contribution Margin', type: 'currency' },
      { key: 'metrics.shopify_total_contribution_margin', label: 'Shopify Total Contribution Margin', type: 'currency' },
      { key: 'metrics.amazon_contribution_margin', label: 'Amazon Contribution Margin', type: 'currency' },
      { key: 'metrics.total_business_contribution_margin', label: 'Total Business Contribution Margin', type: 'currency' },
      { key: 'metrics.contribution_margin_percent', label: 'Ecomm Contribution Margin %', type: 'percentage' },
    ],
    showTotals: false,
  },
  revenueBreakdown: {
    title: 'Baseline Revenue Breakdown',
    isDynamic: true,
    resolveMetrics: getRevenueBreakdownMetrics,
    showTotals: false,
  },
  metaAdsReachImpressions: {
    title: 'Meta Ads Reach & Impressions',
    metrics: [
      { key: 'metrics.facebook_ads_reach', label: 'Meta Ads Unique Reach', type: 'number' },
      { key: 'metrics.facebook_ads_impressions', label: 'Meta Ads Total Impressions', type: 'number' },
    ],
    showTotals: false,
  },
  metaAdsAudienceSegments: {
    title: 'Meta Ads Audience Segments',
    metrics: [
      { key: 'metrics.facebook_ads_reach_prospecting', label: 'Reach — Prospecting', type: 'number' },
      { key: 'metrics.facebook_ads_reach_engaged', label: 'Reach — Engaged', type: 'number' },
      { key: 'metrics.facebook_ads_reach_existing', label: 'Reach — Existing', type: 'number' },
      { key: 'metrics.facebook_ads_reach_unknown', label: 'Reach — Unknown', type: 'number' },
      { key: 'metrics.facebook_ads_reach_uncategorized', label: 'Reach — Uncategorized', type: 'number' },
      { key: 'metrics.facebook_ads_impressions_prospecting', label: 'Impressions — Prospecting', type: 'number' },
      { key: 'metrics.facebook_ads_impressions_engaged', label: 'Impressions — Engaged', type: 'number' },
      { key: 'metrics.facebook_ads_impressions_existing', label: 'Impressions — Existing', type: 'number' },
      { key: 'metrics.facebook_ads_impressions_unknown', label: 'Impressions — Unknown', type: 'number' },
      { key: 'metrics.facebook_ads_impressions_uncategorized', label: 'Impressions — Uncategorized', type: 'number' },
      { key: 'metrics.facebook_ads_spend_prospecting', label: 'Spend — Prospecting', type: 'currency' },
      { key: 'metrics.facebook_ads_spend_engaged', label: 'Spend — Engaged', type: 'currency' },
      { key: 'metrics.facebook_ads_spend_existing', label: 'Spend — Existing', type: 'currency' },
      { key: 'metrics.facebook_ads_spend_unknown', label: 'Spend — Unknown', type: 'currency' },
      { key: 'metrics.facebook_ads_spend_uncategorized', label: 'Spend — Uncategorized', type: 'currency' },
      { key: 'metrics.facebook_ads_link_clicks_prospecting', label: 'Link Clicks — Prospecting', type: 'number' },
      { key: 'metrics.facebook_ads_link_clicks_engaged', label: 'Link Clicks — Engaged', type: 'number' },
      { key: 'metrics.facebook_ads_link_clicks_existing', label: 'Link Clicks — Existing', type: 'number' },
      { key: 'metrics.facebook_ads_link_clicks_unknown', label: 'Link Clicks — Unknown', type: 'number' },
      { key: 'metrics.facebook_ads_link_clicks_uncategorized', label: 'Link Clicks — Uncategorized', type: 'number' },
    ],
    showTotals: false,
  },
  brandValue: {
    title: 'Brand Value',
    metrics: [
      { key: 'metrics.d30_brand_value', label: '30d Brand Value', type: 'currency' },
      { key: 'metrics.d180_brand_value', label: '180d Brand Value', type: 'currency' },
    ],
    showTotals: false,
    marginalMetrics: ['metrics.d180_brand_value'],
  },
  brandValueBreakdown: {
    title: 'Brand Value Breakdown',
    metrics: [
      { key: 'company_platform_brand_value.ad_channel_group_brand_value', label: 'Ad', type: 'currency' },
      { key: 'company_platform_brand_value.mentions_channel_group_brand_value', label: 'Mentions', type: 'currency' },
      { key: 'company_platform_brand_value.organic_channel_group_brand_value', label: 'Organic', type: 'currency' },
      { key: 'company_platform_brand_value.search_channel_group_brand_value', label: 'Search', type: 'currency' },
      { key: 'company_platform_brand_value.subscription_channel_group_brand_value', label: 'Subscription', type: 'currency' },
      { key: 'company_platform_brand_value.unattributed_channel_group_brand_value', label: 'Unattributed', type: 'currency' },
      { key: 'company_platform_brand_value.total_brand_value', label: 'Total Brand Value', type: 'currency' },
    ],
    showTotals: false,
    marginalMetrics: ['company_platform_brand_value.total_brand_value'],
  },
  digitalEngagement: {
    title: 'Digital Engagement',
    metrics: [
      { key: 'metrics.total_likes', label: 'Total Likes/Reactions', type: 'number' },
      { key: 'metrics.total_comments', label: 'Total Comments', type: 'number' },
      { key: 'metrics.total_shares', label: 'Total Shares', type: 'number' },
      { key: 'metrics.total_saves', label: 'Total Saves', type: 'number' },
      { key: 'metrics.total_new_followers', label: 'Total New Followers', type: 'number' },
      { key: 'metrics.total_new_subscribers', label: 'Total Subscribers', type: 'number' },
      { key: 'metrics.total_followers_plus_subscribers', label: 'Total New Followers + Subscribers', type: 'number' },
      { key: 'metrics.youtube_channel_views', label: 'YouTube Views', type: 'number' },
      { key: 'metrics.youtube_ads_video_views', label: 'YouTube Ad Views', type: 'number' },
      { key: 'metrics.total_platform_engagements', label: 'Total Platform Engagements', type: 'number' },
      { key: 'metrics.total_digital_behavior', label: 'Total Digital Behavior', type: 'number' },
    ],
    showTotals: false,
  },
  brandMentionsTags: {
    title: 'Brand Mentions & Tags',
    metrics: [
      { key: 'metrics.total_mentions_count', label: 'Total Mentions', type: 'number' },
      { key: 'metrics.total_tags_count', label: 'Total Tags', type: 'number' },
      { key: 'metrics.total_mentioned_likes', label: 'Total Mentioned Likes', type: 'number' },
      { key: 'metrics.total_tagged_likes', label: 'Total Tagged Likes', type: 'number' },
    ],
    showTotals: false,
  },

  // ---- Custom-report-only: order cohorts + first-order incremental LTV by channel ----
  shopifyEcommOrderCohorts: {
    title: 'Shopify Ecomm - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_ecomm_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_ecomm_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_ecomm_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_ecomm_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifyRetailOrderCohorts: {
    title: 'Shopify DTC Retail - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_dtc_retail_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_dtc_retail_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_dtc_retail_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_dtc_retail_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifySubscriptionOrderCohorts: {
    title: 'Shopify Subscription - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_subscription_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_subscription_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_subscription_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_subscription_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifyThirdPartyEcommOrderCohorts: {
    title: 'Shopify Third Party Ecomm - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_third_party_ecomm_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_ecomm_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_ecomm_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_ecomm_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifyThirdPartyRetailOrderCohorts: {
    title: 'Shopify Third Party Retail - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_third_party_retail_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_retail_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_retail_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_third_party_retail_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifyTotalOrderCohorts: {
    title: 'Shopify Total - Order Counts by Cohort',
    metrics: [
      { key: 'metrics.shopify_total_new_customer_orders', label: 'First Orders', type: 'number' },
      { key: 'metrics.shopify_total_second_customer_orders', label: '2nd Orders', type: 'number' },
      { key: 'metrics.shopify_total_third_customer_orders', label: '3rd Orders', type: 'number' },
      { key: 'metrics.shopify_total_fourth_customer_orders', label: '4th Orders', type: 'number' },
    ],
    showTotals: true,
  },
  shopifyEcommIncrementalLtv: {
    title: 'Shopify Ecomm - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_ecomm_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_ecomm_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_ecomm_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_ecomm_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_ecomm_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifyRetailIncrementalLtv: {
    title: 'Shopify DTC Retail - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_dtc_retail_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_dtc_retail_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifySubscriptionIncrementalLtv: {
    title: 'Shopify Subscription - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_subscription_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_subscription_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_subscription_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_subscription_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_subscription_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifyThirdPartyEcommIncrementalLtv: {
    title: 'Shopify Third Party Ecomm - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_third_party_ecomm_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_ecomm_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_ecomm_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_ecomm_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_ecomm_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifyThirdPartyRetailIncrementalLtv: {
    title: 'Shopify Third Party Retail - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_third_party_retail_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_retail_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_retail_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_retail_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_third_party_retail_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  shopifyTotalIncrementalLtv: {
    title: 'Shopify Total - First-Order Incremental LTV',
    metrics: [
      { key: 'metrics.shopify_total_first_order_incremental_30d_ltv', label: '30-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_total_first_order_incremental_60d_ltv', label: '60-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_total_first_order_incremental_90d_ltv', label: '90-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_total_first_order_incremental_180d_ltv', label: '180-Day Incremental LTV', type: 'currency' },
      { key: 'metrics.shopify_total_first_order_incremental_365d_ltv', label: '365-Day Incremental LTV', type: 'currency' },
    ],
    showTotals: false,
  },
  // Amazon contribution metrics, COMPUTED on the frontend (not from a cube
  // column). "Amazon Contribution $" = Amazon total revenue minus costs, where
  // ad spend = max(real Amazon Ads spend, a default assumption), and the other
  // costs are % of revenue from Financial Settings; the margin is contribution
  // / revenue. The `computed.` key prefix routes these to processComputedMetrics
  // via sourceForMetricKey; the compute fns live in computed-metrics.ts (keep
  // label/type here in sync with that registry — a spec guards it). Catalog-only
  // (absent from BUILTIN_DEFAULT_GROUP_ORDER), so not a default dashboard table.
  amazonContribution: {
    title: 'Amazon Contribution',
    metrics: [
      { key: 'computed.amazon_contribution', label: 'Amazon Contribution', type: 'currency' },
      { key: 'computed.amazon_contribution_margin', label: 'Amazon Contribution Margin %', type: 'percentage' },
    ],
    showTotals: false,
  },
  // Modeled paid-media metrics sourced from the campaign media cube
  // (`company_media_metrics_campaigns`), not the `metrics` cube. They are
  // catalog-only — exposed to custom reports but not rendered as a default
  // dashboard table (so this slug is intentionally absent from
  // BUILTIN_DEFAULT_GROUP_ORDER). The `company_media_metrics_campaigns.` key
  // prefix routes these to their own cube query via `sourceForMetricKey`.
  // ROAS/BROAS are ratio measures the cube already computes per period as
  // SUM(revenue)/SUM(spend), so no per-row math happens in the UI.
  modeledMetrics: {
    title: 'Modeled Metrics',
    metrics: [
      { key: 'company_media_metrics_campaigns.modeled_revenue', label: 'Modeled Revenue', type: 'currency' },
      { key: 'company_media_metrics_campaigns.modeled_revenue_over_spend', label: 'Modeled ROAS', type: 'decimal', suffix: 'x' },
      { key: 'company_media_metrics_campaigns.estimated_d180_brand_value', label: 'Modeled Brand Value', type: 'currency' },
      { key: 'company_media_metrics_campaigns.modeled_brand_value_over_spend', label: 'Modeled BROAS', type: 'decimal', suffix: 'x' },
    ],
    showTotals: false,
  },
  // Channel breakouts of the modeled metrics above, from the same campaign
  // media cube via channel-filtered measures (media_channel = meta /
  // youtube_ads / google_ads / tiktok / pinterest). Catalog-only, like modeledMetrics.
  // Every key here must also be fetched by the KPI page's mediaCampaignsQuery
  // (connect.ts) — the "media campaigns catalog coverage" spec enforces it.
  modeledMetricsByChannel: {
    title: 'Modeled Metrics by Channel',
    metrics: [
      { key: 'company_media_metrics_campaigns.meta_ads_modeled_revenue', label: 'Meta Modeled Revenue', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.meta_ads_modeled_revenue_over_spend',
        label: 'Meta Modeled ROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.meta_ads_modeled_brand_value', label: 'Meta Modeled Brand Value', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.meta_ads_modeled_brand_value_over_spend',
        label: 'Meta Modeled BROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.youtube_ads_modeled_revenue', label: 'YouTube Modeled Revenue', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.youtube_ads_modeled_revenue_over_spend',
        label: 'YouTube Modeled ROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.youtube_ads_modeled_brand_value', label: 'YouTube Modeled Brand Value', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.youtube_ads_modeled_brand_value_over_spend',
        label: 'YouTube Modeled BROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.google_ads_modeled_revenue', label: 'Google Ads Modeled Revenue', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.google_ads_modeled_revenue_over_spend',
        label: 'Google Ads Modeled ROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.google_ads_modeled_brand_value', label: 'Google Ads Modeled Brand Value', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.google_ads_modeled_brand_value_over_spend',
        label: 'Google Ads Modeled BROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.tiktok_ads_modeled_revenue', label: 'TikTok Modeled Revenue', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.tiktok_ads_modeled_revenue_over_spend',
        label: 'TikTok Modeled ROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.tiktok_ads_modeled_brand_value', label: 'TikTok Modeled Brand Value', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.tiktok_ads_modeled_brand_value_over_spend',
        label: 'TikTok Modeled BROAS',
        type: 'decimal',
        suffix: 'x',
      },
      { key: 'company_media_metrics_campaigns.pinterest_ads_modeled_revenue', label: 'Pinterest Modeled Revenue', type: 'currency' },
      {
        key: 'company_media_metrics_campaigns.pinterest_ads_modeled_revenue_over_spend',
        label: 'Pinterest Modeled ROAS',
        type: 'decimal',
        suffix: 'x',
      },
      {
        key: 'company_media_metrics_campaigns.pinterest_ads_modeled_brand_value',
        label: 'Pinterest Modeled Brand Value',
        type: 'currency',
      },
      {
        key: 'company_media_metrics_campaigns.pinterest_ads_modeled_brand_value_over_spend',
        label: 'Pinterest Modeled BROAS',
        type: 'decimal',
        suffix: 'x',
      },
    ],
    showTotals: false,
  },
  // Demand Gen vs Demand Capture rollups of adset-level paid-media spend and
  // modeled metrics (the `initiatives` source — see initiative-metrics.ts).
  // Category membership resolves dynamically per company at pivot time.
  // Catalog-only (intentionally NOT in BUILTIN_DEFAULT_GROUP_ORDER).
  demandCategoryMetrics: {
    title: 'Demand Gen vs Demand Capture',
    metrics: buildInitiativeCategoryMetrics(),
    showTotals: false,
  },
  // Per-initiative-type breakouts of the same adset-level metrics: every
  // predefined initiative type plus the Unassigned bucket. A company's
  // CUSTOM_* types are appended at runtime via initiativeTypeCatalogEntries.
  // Catalog-only (intentionally NOT in BUILTIN_DEFAULT_GROUP_ORDER).
  initiativeTypeMetrics: {
    title: 'Initiative Type Breakouts',
    metrics: buildInitiativeTypeMetrics(),
    showTotals: false,
  },

  // ---- Catalog-only: Metrics Explorer measures exposed to custom reports ----
  // Every group below mirrors measures already charted by the Metrics Explorer
  // (`brand_value_stacked_chart` in charts.tsx), reusing its labels. They are
  // intentionally NOT in BUILTIN_DEFAULT_GROUP_ORDER, so they never render as
  // default dashboard tables — they only make these measures selectable in the
  // custom-report metric picker. All keys are plain `metrics.*` measures
  // fetched by REVENUE_KPI_MEASURES (queries.ts) — the "revenue_kpi_table
  // catalog coverage" spec enforces that pairing. showTotals stays false for
  // all of them: each mixes component rows with rollups (or carries ratios),
  // so a summed WoW Marginal row would double-count.
  brandValueDetail: {
    title: 'Brand Value Detail',
    metrics: [
      { key: 'metrics.d60_brand_value', label: '60d Brand Value', type: 'currency' },
      { key: 'metrics.d90_brand_value', label: '90d Brand Value', type: 'currency' },
      { key: 'metrics.d120_brand_value', label: '120d Brand Value', type: 'currency' },
      { key: 'metrics.d150_brand_value', label: '150d Brand Value', type: 'currency' },
      { key: 'metrics.brand_factor', label: 'Brand Factor', type: 'decimal' },
      { key: 'metrics.monetization_factor', label: 'Monetization Factor', type: 'currency' },
      { key: 'metrics.total_brand_roas', label: 'Total Brand ROAS', type: 'decimal', suffix: 'x' },
    ],
    showTotals: false,
  },
  brandedSearchDetail: {
    title: 'Branded Search Detail',
    metrics: [
      { key: 'metrics.google_ads_paid_branded_search_impressions', label: 'Google Paid Branded Search Impressions', type: 'number' },
      { key: 'metrics.google_search_organic_branded_search_clicks', label: 'Google Organic Branded Search Clicks', type: 'number' },
      {
        key: 'metrics.google_search_organic_branded_search_impressions_unattributed',
        label: 'Unattributed Branded Searches',
        type: 'number',
      },
    ],
    showTotals: false,
  },
  // Single-property GA revenue plus the multi-property variants the dynamic
  // gaRevenue group does not emit. The catalog already labels
  // ga_multi_property_paid_revenue / ga_multi_property_triggered_revenue as
  // plain "GA Paid Revenue" / "GA Triggered Revenue" (dynamic-groups.ts), so
  // the single-property twins here carry an explicit "(Single Property)"
  // suffix — and both total_revenue_from_all_sessions variants state their
  // property scope — to keep the picker unambiguous.
  gaRevenueDetail: {
    title: 'GA Revenue Detail',
    metrics: [
      { key: 'metrics.ga_paid_revenue', label: 'GA Paid Revenue (Single Property)', type: 'currency' },
      { key: 'metrics.ga_triggered_revenue', label: 'GA Triggered Revenue (Single Property)', type: 'currency' },
      {
        key: 'metrics.ga_total_revenue_from_all_sessions',
        label: 'GA Total Revenue from All Sessions (Single Property)',
        type: 'currency',
      },
      {
        key: 'metrics.ga_multi_property_total_revenue_from_all_sessions',
        label: 'GA Total Revenue from All Sessions (Multi-Property)',
        type: 'currency',
      },
      {
        key: 'metrics.ga_multi_property_revenue_baseline_plus_paid_brand_search',
        label: 'GA Multi-Property Baseline + Paid Brand Search Revenue',
        type: 'currency',
      },
      {
        key: 'metrics.ga_multi_property_amazon_baseline_revenue_plus_paid_brand_search',
        label: 'GA Multi-Property Baseline + Paid Brand Search Revenue + Amazon Owned',
        type: 'currency',
      },
    ],
    showTotals: false,
  },
  subscriberDetail: {
    title: 'Subscriber Detail',
    metrics: [{ key: 'metrics.klaviyo_email_subscribers', label: 'Klaviyo Email Subscribers', type: 'number' }],
    showTotals: false,
  },
  adSpendDetail: {
    title: 'Ad Spend Detail',
    // The explorer legend calls this "Paid Social"; the catalog label states
    // the exact scope instead (TikTok + Instagram + Facebook + Pinterest ads spend).
    metrics: [{ key: 'metrics.total_social_ad_spend', label: 'Total Social Ad Spend', type: 'currency' }],
    showTotals: false,
  },
  facebookEngagement: {
    title: 'Facebook Engagement',
    metrics: [
      { key: 'metrics.facebook_ads_likes', label: 'Facebook Ads Likes', type: 'number' },
      { key: 'metrics.facebook_ads_comments', label: 'Facebook Ads Comments', type: 'number' },
      { key: 'metrics.facebook_ads_shares', label: 'Facebook Ads Shares', type: 'number' },
      { key: 'metrics.facebook_ads_follows', label: 'Facebook Ads Follows', type: 'number' },
      { key: 'metrics.facebook_pages_likes', label: 'Facebook Pages Likes', type: 'number' },
      { key: 'metrics.facebook_pages_comments', label: 'Facebook Pages Comments', type: 'number' },
      { key: 'metrics.facebook_pages_shares', label: 'Facebook Pages Shares', type: 'number' },
      { key: 'metrics.facebook_pages_follows', label: 'Facebook Pages Follows', type: 'number' },
      { key: 'metrics.facebook_total_likes', label: 'Facebook Total Likes', type: 'number' },
      { key: 'metrics.facebook_total_comments', label: 'Facebook Total Comments', type: 'number' },
      { key: 'metrics.facebook_total_shares', label: 'Facebook Total Shares', type: 'number' },
      { key: 'metrics.facebook_total_follows', label: 'Facebook Total Follows', type: 'number' },
      { key: 'metrics.facebook_total_saves', label: 'Facebook Total Saves', type: 'number' },
    ],
    showTotals: false,
  },
  instagramEngagement: {
    title: 'Instagram Engagement',
    metrics: [
      { key: 'metrics.instagram_ads_likes', label: 'Instagram Ads Likes', type: 'number' },
      { key: 'metrics.instagram_ads_comments', label: 'Instagram Ads Comments', type: 'number' },
      { key: 'metrics.instagram_ads_shares', label: 'Instagram Ads Shares', type: 'number' },
      { key: 'metrics.instagram_ads_saves', label: 'Instagram Ads Saves', type: 'number' },
      { key: 'metrics.instagram_business_likes', label: 'Instagram Post Likes', type: 'number' },
      { key: 'metrics.instagram_business_comments', label: 'Instagram Post Comments', type: 'number' },
      { key: 'metrics.instagram_business_shares', label: 'Instagram Post Shares', type: 'number' },
      { key: 'metrics.instagram_business_saves', label: 'Instagram Post Saves', type: 'number' },
      { key: 'metrics.instagram_total_likes', label: 'Instagram Total Likes', type: 'number' },
      { key: 'metrics.instagram_total_comments', label: 'Instagram Total Comments', type: 'number' },
      { key: 'metrics.instagram_total_shares', label: 'Instagram Total Shares', type: 'number' },
      { key: 'metrics.instagram_total_saves', label: 'Instagram Total Saves', type: 'number' },
      { key: 'metrics.instagram_total_follows', label: 'Instagram Total Follows', type: 'number' },
    ],
    showTotals: false,
  },
  tiktokEngagement: {
    title: 'TikTok Engagement',
    metrics: [
      { key: 'metrics.tiktok_ads_likes', label: 'TikTok Ads Likes', type: 'number' },
      { key: 'metrics.tiktok_ads_comments', label: 'TikTok Ads Comments', type: 'number' },
      { key: 'metrics.tiktok_ads_shares', label: 'TikTok Ads Shares', type: 'number' },
      { key: 'metrics.tiktok_ads_saves', label: 'TikTok Ads Saves', type: 'number' },
      { key: 'metrics.tiktok_ads_follows', label: 'TikTok Ads Follows', type: 'number' },
      { key: 'metrics.tiktok_videos_likes', label: 'TikTok Organic Likes', type: 'number' },
      { key: 'metrics.tiktok_videos_comments', label: 'TikTok Organic Comments', type: 'number' },
      { key: 'metrics.tiktok_videos_shares', label: 'TikTok Organic Shares', type: 'number' },
      { key: 'metrics.tiktok_total_likes', label: 'TikTok Total Likes', type: 'number' },
      { key: 'metrics.tiktok_total_comments', label: 'TikTok Total Comments', type: 'number' },
      { key: 'metrics.tiktok_total_shares', label: 'TikTok Total Shares', type: 'number' },
      { key: 'metrics.tiktok_total_follows', label: 'TikTok Total Follows', type: 'number' },
    ],
    showTotals: false,
  },
  youtubeEngagement: {
    title: 'YouTube Engagement',
    metrics: [
      { key: 'metrics.youtube_channel_likes', label: 'YouTube Total Likes', type: 'number' },
      { key: 'metrics.youtube_channel_comments', label: 'YouTube Total Comments', type: 'number' },
      { key: 'metrics.youtube_channel_shares', label: 'YouTube Total Shares', type: 'number' },
      { key: 'metrics.youtube_channel_saves', label: 'YouTube Total Saves', type: 'number' },
      { key: 'metrics.youtube_channel_follows', label: 'YouTube Total Follows', type: 'number' },
    ],
    showTotals: false,
  },
} as const satisfies Record<string, MetricGroupSpec>;

export type KpiTableSlug = keyof typeof KPI_METRIC_GROUPS;
